import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import * as Sentry from '@sentry/react'
import { useCreateMemecoinMutation } from '../../../modules/memecoins/api'
import { links } from '../../../router/routes'
import useDeployToChain, { MEMECOIN_TOKEN_DECIMALS } from './useDeployToChain'
import { Box } from '../../../shared_components/UI/Box'
import { PoolInput } from '../../PoolDetails/parts'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { FormBox, InputBox, InputLabel, RowInputBox } from './styles'
import ImageUploader from './ImageUploader'
import { H1, H2, P } from '../../../shared_components/Library/Typeography'
import Separator from '../../../shared_components/Library/Separator'
import { EthIconLabel } from '../../../shared_components/Library/Chains'
import useNetworkId from '../../../hooks/useNetworkId'
import { ethers } from 'ethers'
import useIsLoggedIn from '../../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../../shared_components/Library/Buttons/ConnectWalletButton'
import SwitchToBaseButton from '../../../shared_components/Library/Buttons/SwitchToBaseButton'
import { addTokenToWallet } from '../TokenPage/Invest'
import useWeb3 from '../../../hooks/useWeb3'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { BackButton } from '../../../shared_components/Page'
import { useShouldShowForThreshold } from '../../../theme'

const { REACT_APP_NETWORK } = process.env

const TokenLauncher = () => {
  const { isLoggedIn } = useIsLoggedIn()
  const { wrongNetwork } = useNetworkId()
  const { address: walletAddress } = useIsLoggedIn()
  const isAtLeastExpandedDisplaySize = useShouldShowForThreshold('medium')

  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
    handleSubmit,
  } = useForm({ mode: 'onBlur' })
  const { push } = useHistory()

  const { networkId } = useNetworkId()
  const { primaryWallet } = useDynamicContext()
  const { provider } = useWeb3()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [createMemecoin] = useCreateMemecoinMutation()
  const { prepareTransaction } = useDeployToChain()

  const logoUrl = watch('logoUrl', '')
  useEffect(() => {
    register('logoUrl', { required: true })
  }, [register])

  const handleSetLogoUrl = useCallback(
    (url: string) => {
      setValue('logoUrl', url)
      trigger('logoUrl')
    },
    [setValue, trigger],
  )
  const handleSetBannerUrl = useCallback(
    (url: string) => {
      setValue('bannerUrl', url)
      trigger('bannerUrl')
    },
    [setValue, trigger],
  )

  const onSubmit = useCallback(
    async ({
      tokenName,
      tokenSymbol,
      description,
      logoUrl: formLogoUrl,
      bannerUrl: formBannerUrl,
      twitterUrl,
      telegramUrl,
      discordUrl,
      websiteUrl,
      initialBuy,
    }: {
      tokenName: string
      tokenSymbol: string
      description: string
      logoUrl: string
      bannerUrl: string
      twitterUrl: string
      telegramUrl: string
      discordUrl: string
      websiteUrl: string
      initialBuy: string
    }) => {
      try {
        setIsSubmitting(true)
        setError('')

        const isMainnet = REACT_APP_NETWORK === 'mainnet'
        if (isMainnet) {
          const isTestToken =
            tokenSymbol.toLowerCase().includes('test') ||
            tokenName.toLowerCase().includes('test')

          if (isTestToken) {
            setError(
              'Token with keyword "test" is not allowed. Please use a different name or symbol.',
            )
            return
          }
        }

        const { populatedTx, txHash, signer, contractInstance, error } =
          await prepareTransaction({
            initialBuyAmountInEth: Number(initialBuy),
            tokenName,
            tokenSymbol,
          })

        if (error) {
          setError(error)
          return
        }
        if (!signer) throw new Error('No signer found')
        if (!txHash) throw new Error('No txHash found')
        if (!contractInstance) throw new Error('No contractInstance found')
        if (!populatedTx) throw new Error('No unsigned transaction found')

        await createMemecoin({
          tokenName,
          tokenSymbol,
          description,
          txHash,
          logoUrl: formLogoUrl,
          bannerUrl: formBannerUrl,
          socials: [
            {
              label: 'X',
              url: twitterUrl,
            },
            {
              label: 'Telegram',
              url: telegramUrl,
            },
            {
              label: 'Discord',
              url: discordUrl,
            },
            {
              label: 'Website',
              url: websiteUrl,
            },
          ],
          chainId: networkId,
        }).unwrap()

        const { poolAddress, tokenAddress, creatorAddress } =
          await sendTransactionAndGetEvents(
            signer,
            populatedTx,
            contractInstance,
          )

        if (!poolAddress) throw new Error('No created pool address')
        if (!tokenAddress) throw new Error('No created token address')
        if (!creatorAddress) throw new Error('No creator address')

        console.info('Token created:', {
          poolAddress,
          tokenAddress,
          creatorAddress,
        })

        await addTokenToWallet(
          tokenAddress,
          tokenSymbol,
          MEMECOIN_TOKEN_DECIMALS,
          provider,
          primaryWallet,
        )

        push(links.memecoins.address(poolAddress))

        toast.success('Token created successfully')
      } catch (error) {
        Sentry.captureException(error)
        console.error(error)
        toast.error('Something went wrong')
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      prepareTransaction,
      createMemecoin,
      push,
      networkId,
      primaryWallet,
      walletAddress,
    ],
  )

  async function sendTransactionAndGetEvents(
    signer: ethers.Signer,
    populatedTx: ethers.ContractTransaction,
    contract: ethers.BaseContract,
  ) {
    const txResponse = await signer.sendTransaction(populatedTx)

    console.info('Transaction sent:', txResponse.hash)

    const receipt = await txResponse.wait()

    const parsedLogs = receipt.logs
      .map((log) => {
        try {
          return contract.interface.parseLog(log)
        } catch (error) {
          console.info('Could not parse log:', log)
          return null
        }
      })
      .filter(Boolean)

    console.log('parsedLogs', parsedLogs)

    const addresses = {
      tokenAddress: '',
      poolAddress: '',
      creatorAddress: '',
    }

    parsedLogs.forEach((log) => {
      if (log.name !== 'LiquidityFreeLaunchCreated') return
      const { args } = log
      if (!args) return
      const [poolAddress, tokenAddress, creatorAddress] = args
      addresses.tokenAddress = tokenAddress
      addresses.poolAddress = poolAddress
      addresses.creatorAddress = creatorAddress
    })

    console.log('addresses', addresses)

    return { ...addresses }
  }

  return (
    <Container dc g={32}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormBox>
          <Box dc g={32}>
            {isAtLeastExpandedDisplaySize && <BackButton padT={0} />}
            <H2 s="2XL" fw="semibold">
              Launch your token
            </H2>
          </Box>
          <RowInputBox>
            <InputBox>
              <InputLabel htmlFor="tokenName" required>
                Token Name
              </InputLabel>
              <PoolInput
                id="tokenName"
                name="tokenName"
                type="text"
                {...register('tokenName', { required: true, maxLength: 32 })}
              />
              {errors.tokenName && (
                <ErrorMessage>Token name is required</ErrorMessage>
              )}
            </InputBox>
            <InputBox>
              <InputLabel htmlFor="tokenSymbol" required>
                Token Symbol
              </InputLabel>
              <PoolInput
                id="tokenSymbol"
                name="tokenSymbol"
                type="text"
                {...register('tokenSymbol', { required: true, maxLength: 6 })}
              />
              {errors.tokenSymbol && (
                <ErrorMessage>
                  Token symbol must be between 1 and 6 characters
                </ErrorMessage>
              )}
            </InputBox>
          </RowInputBox>
          <InputBox>
            <InputLabel htmlFor="description">Token Description</InputLabel>
            <textarea
              id="description"
              name="description"
              rows={8}
              {...register('description', { required: true })}
            />
            {errors.description && (
              <ErrorMessage>A description is required</ErrorMessage>
            )}
          </InputBox>
          <RowInputBox>
            <Box dc g={16}>
              <ImageUploader
                label={<InputLabel>Upload an image for your token</InputLabel>}
                subLabel="For best results use a square image, at least 800x800px, GIF, PNG or JPEG"
                imageUrl={logoUrl}
                setImageUrl={handleSetLogoUrl}
                width="104"
                height="104"
              />
              {errors.logoUrl && (
                <ErrorMessage>An image is required</ErrorMessage>
              )}
            </Box>
          </RowInputBox>
          <Separator />
          <RowInputBox>
            <InputBox>
              <InputLabel htmlFor="twitterUrl">Add X (Twitter)</InputLabel>
              <PoolInput
                id="twitterUrl"
                name="twitterUrl"
                type="url"
                placeholder="https://x.com/..."
                {...register('twitterUrl', { required: false })}
              />
            </InputBox>
            <InputBox>
              <InputLabel htmlFor="telegramUrl">Add Telegram</InputLabel>
              <PoolInput
                id="telegramUrl"
                name="telegramUrl"
                type="url"
                placeholder="https://t.me/..."
                {...register('telegramUrl', { required: false })}
              />
            </InputBox>
          </RowInputBox>
          <RowInputBox>
            <InputBox>
              <InputLabel htmlFor="discordUrl">Add Discord</InputLabel>
              <PoolInput
                id="discordUrl"
                name="discordUrl"
                type="url"
                placeholder="https://discordUrl.gg/..."
                {...register('discordUrl', { required: false })}
              />
            </InputBox>
            <InputBox>
              <InputLabel htmlFor="websiteUrl">Add Website</InputLabel>
              <PoolInput
                id="websiteUrl"
                name="websiteUrl"
                type="url"
                placeholder="https://..."
                {...register('websiteUrl', { required: false })}
              />
            </InputBox>
          </RowInputBox>
          <InputBox>
            <InputLabel htmlFor="initialBuy">
              <Box dc g={8}>
                <span>Buy tokens at creation</span>
                <P s="S" c="text-secondary">
                  Tokens with an initial dev buy are more likely to succeed. You
                  can not exceed 5% of the supply
                </P>
              </Box>
            </InputLabel>
            <Box g={12} ac>
              <PoolInput
                id="initialBuy"
                name="initialBuy"
                type="number"
                step="any"
                {...register('initialBuy', { required: false })}
              />
              <EthIconLabel />
            </Box>
          </InputBox>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Box fu>
            {!isLoggedIn ? (
              <ConnectWalletButton />
            ) : wrongNetwork ? (
              <SwitchToBaseButton $styleType="primary" />
            ) : (
              <ThemedButton
                $styleType="primary"
                type="submit"
                disabled={isSubmitting}
              >
                <Box g={8} ac>
                  {isSubmitting ? (
                    <>
                      <AnimatedLoader size={14} /> Launching...
                    </>
                  ) : (
                    <>Launch token</>
                  )}
                </Box>
              </ThemedButton>
            )}
          </Box>
        </FormBox>
      </form>
    </Container>
  )
}

export default TokenLauncher

const Container = styled(Box)`
  padding: 2rem;

  form {
    width: 100%;
    max-width: 768px;
    margin: 0 auto;
  }
`

const FadedText = styled.span`
  opacity: 0.5;
  font-size: 14px;
`

const ErrorMessage = styled.div`
  color: #fc8181;
  font-size: 14px;
`
